import {Container, Sprite} from 'pixi.js';
import {ReelConstants, SymbolPosition, SymbolsOrdering} from '@gongxigames/framework/slot/utils';
import {ReelsConfig} from '@gongxigames/framework/slot/types';
import {consoleLogger, DisplayObjectsPool, IAnimation, MovieClip, ObjectBuilder, SpineUtils, UpdateManager} from '@gongxigames/framework/core';
import {ReelSymbol, RubyFrameIndex} from '@gongxigames/framework/slot/modules/reels/ReelSymbol';
import {StackedSymbol} from '@gongxigames/framework/slot/modules/reels/StackedSymbol';

/**
 * TODO: 1. put state-based _logic of reel spinning (start of the spin, spin in progress, before stopping tween)
 * TODO: into separated nodes
 *
 * TODO: 2. refactor
 */

export type RowsIteratorCallback = (value: number) => void;

export interface ReelViewData {
    index: number;
    reelSpeed: number;
    startPosition: number;
    reelsContainer: Container;
    animationContainer: Container;
    animationToBeMasked: Container;
    offsets: SymbolPosition;
    reelsConfig: ReelsConfig;
    symbolsPool: DisplayObjectsPool<Sprite>;
    reelConstants?: typeof ReelConstants;
}

export class ReelView extends UpdateManager {
    protected _animationUpdater: UpdateManager = new UpdateManager(true);

    // reel symbols
    protected _symbols: ReelSymbol[];

    // reel offsets
    protected _offsets: SymbolPosition;

    // reel strip
    protected _strip: number[] = [];

    protected _objectBuilder!: ObjectBuilder;

    protected _spineUtils!: SpineUtils;

    // reel state flags
    protected _isSpinning = false;

    // stopped completely including bounce
    protected _isStopped = true;

    // all stopping animations finished
    protected _isHitAnimationsFinished: boolean | undefined = undefined;

    protected _isExpectedToStop = false;

    // positions in terms of reel strip
    protected _currentPosition: number;
    protected _stopPosition?: number;

    // symbols we need to add to the top of the reel before it's final stop
    protected _symbolsLeftToStop = 0;

    // current offset from top of the reel
    protected _movedOffset = 0;

    // reel start stopping callback
    protected _onReelStopping?: Function;

    // reel stopped callback
    protected _onReelStopped?: Function;

    // reel animation finish callback
    protected _onReelAnimationFinished?: Function;

    protected _scatterOnReelThatTriggerFreeGames?: number[];

    // container of the reel (reel itself). used for movement and tweening
    protected _container: Container;

    // container of the reel animations
    protected _animationContainer: Container;

    // container of the reel animations that should under reel frame
    protected _animationToBeMasked: Container;

    protected _oversizeHitContainer!: Container;

    protected _hasHitAnimation = false;

    protected _reelIndex: number;

    protected _reelSpeed: number;

    protected _backgroundContainer?: Container;

    protected _onScatterHitAnimationCallback?: Function;

    protected _onBounceStoppingStartedCallback?: Function;

    protected _reelConstants: typeof ReelConstants;

    protected _symbolsWithHitAnimation: number[][] = [];

    protected _oversizeHitSymbols: number[] = [];

    protected _hitAnimationsCount = 0;

    protected _oversizesHitCount = 0;

    protected _hitSymbolAnimations?: IAnimation[];

    protected _updatingResultSymbols = false;

    protected _additionalBottomResultSymbolsAmount = 0;

    protected _reelsConfig: ReelsConfig;

    protected _oversizeHitStarted = false;

    protected _reelHitOversizes!: Container[];

    // wont work with a strip with 2 stacked symbols together [4, 5, 1, 1, 1, 1, 1, 1, 3]
    protected _stackedSymbols?: StackedSymbol[];

    protected _symbolsPool: DisplayObjectsPool<Sprite>;

    protected _isBlurSymbols = false;

    constructor(reelData: ReelViewData) {
        super();

        this._symbols = [];
        this._reelIndex = reelData.index;
        this._reelSpeed = reelData.reelSpeed;
        this._currentPosition = reelData.startPosition;
        this._container = reelData.reelsContainer;
        this._animationContainer = reelData.animationContainer;
        this._animationToBeMasked = reelData.animationToBeMasked;
        this._offsets = reelData.offsets;
        this._reelsConfig = reelData.reelsConfig;
        this._symbolsPool = reelData.symbolsPool;
        this._reelConstants = reelData.reelConstants || ReelConstants;
        this.add(this._animationUpdater);
    }

    public init(objectBuilder: ObjectBuilder, spineUtils: SpineUtils): void {
        this._objectBuilder = objectBuilder;
        this._spineUtils = spineUtils;
        this._stackedSymbols = this._reelsConfig.stackedSymbols;

        this.createReel();

        this.updateStackedSymbols();

        // prevent tween duration to be negative in turbo mode
        const originalGetStoppingTweenDuration = this.getStoppingTweenDuration;

        this.getStoppingTweenDuration = () => {
            return Math.max(0, originalGetStoppingTweenDuration.call(this));
        };
    }

    public startSpinning(): void {
        if (this._isSpinning) {
            throw new Error('ReelView::startSpinning spinning is already in progress');
        }

        this._animationContainer.children.length = 0;
        this._scatterOnReelThatTriggerFreeGames = undefined;
        this._symbolsWithHitAnimation = [];
        this._isSpinning = true;
        this._isStopped = false;
        this._isHitAnimationsFinished = false;
        this._isExpectedToStop = false;
        this._movedOffset = 0;
        this._updatingResultSymbols = false;
        this._isBlurSymbols = true;

        this.updateStackedSymbols();
        this.updateSymbolsOnSpinning();
    }

    public startStopping(stopPositionIndex: number): void {
        if (!this._isSpinning || this._isExpectedToStop) {
            return;
        }

        const {ROW_AMOUNT, Y_ADDITIONAL_SYMBOLS_OFFSET, REEL_STOPPING_SOUND_DURATION, getReelFullStoppingTime} = this._reelConstants;

        this.updateSymbolsOrder();

        this._isExpectedToStop = true;
        this._additionalBottomResultSymbolsAmount = this.calculateAdditionalBottomResultSymbolsForStackedSymbols(stopPositionIndex);

        this._symbolsLeftToStop = ROW_AMOUNT + Math.abs(Y_ADDITIONAL_SYMBOLS_OFFSET) + this._additionalBottomResultSymbolsAmount;
        this._stopPosition = stopPositionIndex;

        // start to play stopping soundManager exactly in moment to have it finished to the moment when the reel stops
        this._onBounceStoppingStartedCallback && this.addTimer(() => this._onBounceStoppingStartedCallback!(),
            getReelFullStoppingTime() - REEL_STOPPING_SOUND_DURATION, true);
    }

    protected removeBlur(): void {
        this.reelSymbols.forEach(symbol => symbol.removeBlur());
    }

    public update(dt: number): void {
        super.update(dt);

        if (this._isSpinning) {
            this.moveSymbolsByOffset(dt);

            if (this.needToReorderSymbols()) {
                this.reorderSymbols();
                this.updateSymbols();
            }
        }

        this._symbols.forEach((symbol) => {
            symbol.updateAnimationContainerPosition();
        });
    }

    public hideSymbolAt(position: number): void {
        this._symbols[this.rowIndexToSymbolIndex(position)].visible = false;
    }

    public showSymbolAt(position: number): void {
        this._symbols[this.rowIndexToSymbolIndex(position)].visible = true;
    }

    public replaceSymbolAt(position: number, symbolId: number): void {
        this._symbols[this.rowIndexToSymbolIndex(position)].setSymbol(symbolId);
    }

    public setSymbolOnTop(symbol: ReelSymbol): void {
        const parent = symbol.parent;

        parent.setChildIndex(symbol, parent.children.length - 1);

        if (symbol.animationContainer) {
            const animationParent = symbol.animationContainer.parent;

            animationParent.setChildIndex(symbol.animationContainer, animationParent.children.length - 1);
        }
    }

    public setMainGameBackground(): void {
        this.showSymbolsBackground('bg');
        this.showSymbolsBackground('bg_fg', false);
        this.showSymbolsBackground('bg_respin', false);
    }

    public setFreeGamesBackground(): void {
        this.showSymbolsBackground('bg', false);
        this.showSymbolsBackground('bg_fg');
        this.showSymbolsBackground('bg_respin', false);
    }

    protected showSymbolsBackground(name: string, shouldShow = true): void {
        if (this._backgroundContainer) {
            this._backgroundContainer.children.forEach((child) => {
                const background = (child as Sprite).getChildByName(name);

                if (background) {
                    background.visible = shouldShow;
                }
            });
        }
    }

    public setStopPositionForRestore(stopPosition: number): void {
        this._stopPosition = stopPosition;
    }

    public stopSymbolHitAnimations(): void {
        if (this._hitSymbolAnimations && this._hitSymbolAnimations.length > 0) {
            this._hitSymbolAnimations.forEach((animation, row) => {
                if (animation.isPlaying) {
                    animation.stop();
                    this.onSymbolHitAnimationFinished(row);
                }
            });
        }
    }

    protected calculateAdditionalBottomResultSymbolsForStackedSymbols(stopPositionIndex: number): number {
        if (!this._stackedSymbols) {
            return 0;
        }

        let amount = 0;

        this._stackedSymbols.forEach(symbol => {
            for (let i = stopPositionIndex + this._reelConstants.ROW_AMOUNT + 1;
                i < stopPositionIndex + this._reelConstants.ROW_AMOUNT + symbol.symbolsAmount;
                i++) {

                // is part of stacked symbol
                if (this._strip[i] === symbol.symbolType && this._strip[i] === this._strip[i - 1]) {
                    amount++;
                } else { // stacked symbol ended
                    break;
                }
            }
        });

        return amount;
    }

    protected updateStackedSymbols(): void {
        for (let row = 0; row <= this._symbols.length - 1; row++) {
            const symbol = this._symbols[row];
            const {symbolType} = symbol;

            if (this.isStackedSymbol(symbolType)) {
                const symbolsAmount = this.getStackedSymbol(symbolType).symbolsAmount;
                let partsInStack = row === 0 ? 1 : 0;

                // define which symbol is the head of the stack
                if (row === 0) {
                    // for the first symbol on reel we analyze following symbols in order to define is current the head of the stack
                    for (let j = 1; j <= this._symbols.length - 1; j++) {
                        if (this._symbols[j].symbolType === symbolType) {
                            partsInStack++;
                        } else {
                            break;
                        }
                    }
                } else {
                    // for all other symbols on reel we analyze previous symbols in order to define is current the head of the stack
                    for (let j = row - 1; j >= 0; j--) {
                        if (this._symbols[j].symbolType === symbolType) {
                            partsInStack++;
                        } else {
                            break;
                        }
                    }
                }
                // show head of the stacked symbol and hide all other parts
                symbol.visible = partsInStack % symbolsAmount === 0;
                // show the whole symbol
                (symbol.visibleSymbol as MovieClip).gotoAndStop(1);
            }
        }
    }

    protected getStackedSymbol(symbolID: number): StackedSymbol {
        for (const symbol of this._stackedSymbols!) {
            if (symbol.symbolType === symbolID) {
                return symbol;
            }
        }

        throw new Error();
    }

    protected isStackedSymbol(symbolType: number): boolean {
        let isStacked = false;

        if (this._stackedSymbols) {
            this._stackedSymbols.forEach((stackedSymbolData) => {
                if (!isStacked) {
                    isStacked = stackedSymbolData.symbolType === symbolType;
                }
            });
        }

        return isStacked;
    }

    protected stopLastSymbolWithTween(): void {
        const {SYMBOL_SLOT, REEL_BOUNCE_EFFECT} = this._reelConstants;
        // offset of symbol while movement
        const offset: number = this._movedOffset % SYMBOL_SLOT.height;
        const distanceBeforeStop: number = SYMBOL_SLOT.height - offset;
        const posY = {current: 0, previous: 0};
        let isTrailStopped = true;

        this._isSpinning = false;

        this.addTween(posY)
            .to({current: distanceBeforeStop}, this.getStoppingTweenDuration())
            .easing(REEL_BOUNCE_EFFECT)
            .onStart(this.onStartStoppingTween.bind(this, distanceBeforeStop))
            .onUpdate(() => {
                // update y-pos of each symbol on the reel in order to update position of related animation
                this._symbols.forEach((symbol) => {
                    symbol.y += posY.current - posY.previous;
                });

                if (this._backgroundContainer) {
                    this._backgroundContainer.y += posY.current - posY.previous;
                }

                // Hit oversize symbols in the lowest bounce point
                if (posY.current < posY.previous && !this._oversizeHitStarted) {
                    this.hitOversizeSymbols(true);
                }
                posY.previous = posY.current;

                if (this._isBlurSymbols && posY.current >= distanceBeforeStop) {
                    this.stopBlur();
                }

                if (posY.current >= distanceBeforeStop / 2 && isTrailStopped) {
                    isTrailStopped = false;
                    this.stopTrailAnimations();
                }
            })
            .onComplete(() => {
                this._symbols.forEach((symbol) => {
                    symbol.updateAnimationContainerPosition();
                });

                this.stopLastSymbolTweenCompleted();
            })
            .start();

        this.initOversizeHitSymbols();
    }

    protected stopTrailAnimations(): void {
        this._symbols.forEach((symbol) => {
            symbol.stopTrailAnimation();
        });
    }

    protected getStoppingTweenDuration(): number {
        const {SYMBOL_SLOT, REEL_BOUNCE_TIME, getReelSpeed} = this._reelConstants;

        return REEL_BOUNCE_TIME - this._additionalBottomResultSymbolsAmount * SYMBOL_SLOT.height / getReelSpeed();
    }

    protected onStartStoppingTween(distanceBeforeStop: number): void {
        this._hasHitAnimation = false;

        this.hitSymbolsWithAnimation();
        this.hitOversizeSymbols();

        this._onReelStopping && this._onReelStopping(this._reelIndex, distanceBeforeStop);
    }

    protected stopBlur(): void {
        this._isBlurSymbols = false;
        this.removeBlur();
    }

    protected hitSymbolsWithAnimation(): void {
        this._hitAnimationsCount = 0;

        this._symbolsWithHitAnimation.forEach(symbols => {
            this._hitAnimationsCount += symbols.length;
        });

        if (this._hitAnimationsCount > 0) {
            this.addSymbolsHitAnimations();
        }

        this.checkAndAddScatterHitAnimation();
        this.reOrderOverlays();
    }

    protected addSymbolsHitAnimations(): void {
        this._symbolsWithHitAnimation.forEach((rows, symbolType) => {
            for (const row of rows) {
                this.addHitAnimation(symbolType, row);
            }
        });
    }

    /**
     * TODO: should check if we can have several scatters on reel and refactor this function
     */
    protected checkAndAddScatterHitAnimation(): void {
        if (this._scatterOnReelThatTriggerFreeGames) {
            this._scatterOnReelThatTriggerFreeGames.forEach((scatterIndex, row) => {
                this._hitAnimationsCount++;

                this.addHitAnimation(this.scatterIndex, row);
                this._onScatterHitAnimationCallback && this._onScatterHitAnimationCallback();
            });
        }
    }

    protected addHitAnimation(symbolType: number, row: number): void {
        const animation = this.createSymbolHitAnimation(symbolType);
        const symbol = this._symbols[this.rowIndexToSymbolIndex(row - 1)];

        if (animation === null) {
            consoleLogger.warn(`SymbolType ${symbolType} hit animation is null!`);

            return;
        }

        if (!this._hasHitAnimation) {
            this._hasHitAnimation = true;
            this._hitSymbolAnimations = [];
        }

        symbol.animationContainer = new Container();
        symbol.animationContainer.addChild(animation.container);
        symbol.updateAnimationContainerPosition();
        this._animationContainer.addChild(symbol.animationContainer);

        animation.container.x = this._reelConstants.SYMBOL.width / 2;
        animation.container.y = this._reelConstants.SYMBOL.height / 2;
        animation.start();
        animation.setOnComplete(this.onSymbolHitAnimationFinished.bind(this, row));
        this.animationUpdater.add(animation);

        this._hitSymbolAnimations![row] = animation;
    }

    protected initOversizeHitSymbols(): void {
        if (this._oversizesHitCount === 0) {
            this._oversizesHitCount = this._oversizeHitSymbols.filter(symbol => symbol >= 0).length;

            if (this._oversizesHitCount > 0) {
                this._reelHitOversizes = [];
                this._oversizeHitContainer = new Container();
                this._animationContainer.addChild(this._oversizeHitContainer);
            }
        }

        if (this._oversizesHitCount > 0) {
            this._oversizeHitSymbols.forEach((symbolType, row) => {
                const staticSymbol = this.createReelSymbol();
                const symbolsSprite = this.getSpriteFromPool('symbols');
                const reelSymbol = this._symbols[this.rowIndexToSymbolIndex(row - 1)];

                staticSymbol.init(symbolsSprite);
                staticSymbol.setSymbol(symbolType);

                reelSymbol.animationContainer = new Container();
                reelSymbol.updateTransform();
                reelSymbol.animationContainer.addChild(staticSymbol);
                reelSymbol.animationContainer.visible = false;
                this._oversizeHitContainer.addChild(reelSymbol.animationContainer);
                this._reelHitOversizes[row] = reelSymbol.animationContainer;
            });
        }
    }

    protected hitOversizeSymbols(isFirstRow = false): void {
        const hitSymbol = (row: number): void => {
            const symbolContainer = this._reelHitOversizes[row];
            const symbolAnimationContainer = this._symbols[this.rowIndexToSymbolIndex(row - 1)];

            symbolAnimationContainer.hideSymbol();
            symbolContainer.visible = true;
            this._updateManager!.addTimer(this.onOversizeHitCompleted.bind(this), this._reelConstants.REEL_BOUNCE_TIME, true);
        };

        if (this._oversizesHitCount > 0) {
            this._oversizeHitSymbols.forEach((symbolType, row) => {
                if (isFirstRow) {
                    this._oversizeHitStarted = true;

                    if (row !== this._reelConstants.ROW_AMOUNT - 1) {
                        hitSymbol(row);
                    }
                } else {
                    if (row === this._reelConstants.ROW_AMOUNT - 1) {
                        hitSymbol(row);
                    }
                }
            });
        }
    }

    protected getSpriteFromPool(name: string): Sprite {
        return this._symbolsPool.get(name);
    }

    protected onOversizeHitCompleted(): void {
        this._oversizesHitCount--;

        if (this._oversizesHitCount === 0) {
            this._oversizeHitStarted = false;
            this._oversizeHitContainer.removeChildren();
            this._animationContainer.removeChild(this._oversizeHitContainer);
        }
    }

    protected createSymbolHitAnimation(symbolIndex: number): IAnimation | null {
        let animation = null;

        if (symbolIndex === this.scatterIndex) {
            animation = this._spineUtils.createCachedAnimation('scatter', 'scatter_stop');
        }

        return animation;
    }

    protected onSymbolHitAnimationFinished(rowIndex: number): void {
        const showHitSymbol = (symbolType: number, row: number): void => {
            if (row === rowIndex) {
                this._symbols[this.rowIndexToSymbolIndex(row)].setSymbol(symbolType);
                this.showSymbolAt(row);
            }
        };

        if (this._symbolsWithHitAnimation) {
            this._symbolsWithHitAnimation.forEach((rows, symbolType) => {
                for (const row of rows) {
                    showHitSymbol(symbolType, row);
                }
            });
        }

        if (this._scatterOnReelThatTriggerFreeGames) {
            this._scatterOnReelThatTriggerFreeGames.forEach((scatterIndex, row) => {
                showHitSymbol(scatterIndex, row);
            });
        }

        this._hitAnimationsCount--;

        if (this._hitAnimationsCount === 0) {
            this._isHitAnimationsFinished = true;
            this._onReelAnimationFinished && this._onReelAnimationFinished(this._reelIndex);
        }
    }

    protected stopLastSymbolTweenCompleted(): void {
        this._isStopped = true;

        this._symbols.forEach((symbol) => {
            symbol.y = this._offsets.y + this.getYPositionForRow(symbol.index);
            symbol.animationContainer = undefined;
        });

        this._currentPosition = this.makeNormalizedPosition(this._currentPosition - 1);
        this.updateStoppingSymbols();

        if (this._hasHitAnimation) {
            this.hideSymbolsUnderStoppingAnimations();
        }
        this.updateSymbolsOrder();
        this.updateStackedSymbols();

        // calling callback for reel animation finished if there is no dedicated stopping animation
        if (this._hitAnimationsCount === 0) {
            this._isHitAnimationsFinished = true;
            this._onReelAnimationFinished && this._onReelAnimationFinished(this._reelIndex);
        }

        // reel stopped callback calling
        this._onReelStopped && this._onReelStopped(this._reelIndex);
    }

    protected updateSymbols(): void {
        if (this._isExpectedToStop && !this.preventStopping()) {
            if (this._isSpinning) {
                this.updateStoppingSymbols();
            }

            this._symbolsLeftToStop--;

            if (this._symbolsLeftToStop <= 0) {
                this.stopLastSymbolWithTween();
            }
        } else {
            this.updateSymbolsOnSpinning();
        }

        if (!this._isBlurSymbols) {
            this.removeBlur();
        }

        this.updateStackedSymbols();
    }

    protected preventStopping(): boolean {
        // we can prevent stopping only before updating of result symbols
        if (!this._stackedSymbols || this._updatingResultSymbols) {
            return false;
        }

        for (const symbol of this._stackedSymbols) {
            if (this._symbols[0].symbolType === symbol.symbolType) {
                const symbolsToCheck = this._symbols.slice(0, symbol.symbolsAmount);
                const isStacked = symbolsToCheck.every(value => value.symbolType === symbol.symbolType);

                // if we have visible full stacked symbol we don't need to prevent stopping
                return !isStacked;
            }
        }

        return false;
    }

    protected moveSymbolsByOffset(dt: number): void {
        const offset = dt * this._reelSpeed;

        this._symbols.forEach((symbol) => {
            symbol.y += offset;
        });
        this._movedOffset += offset;

        if (this._backgroundContainer) {
            this._backgroundContainer.y += offset;
        }
    }

    protected needToReorderSymbols(): boolean {
        return this._movedOffset >= this._reelConstants.SYMBOL_SLOT.height;
    }

    protected reorderSymbols(): void {
        const newOffset: number = this._movedOffset % this._reelConstants.SYMBOL_SLOT.height;

        this._symbols.forEach((symbol) => {
            symbol.y = this._offsets.y + this.getYPositionForRow(symbol.index) + newOffset;
        });
        this._movedOffset = newOffset;

        if (this._backgroundContainer) {
            this._backgroundContainer.y = newOffset;
        }

        // getting next position from strip
        this._currentPosition = this.makeNormalizedPosition(this._currentPosition - 1);
    }

    protected getYPositionForRow(rowIndex: number): number {
        return rowIndex * this._reelConstants.SYMBOL_SLOT.height;
    }

    protected createReel(): void {
        this._backgroundContainer = new Sprite();
        this._container.addChild(this._backgroundContainer);
        // creating additional render-buffer of symbol from both sides of the reels
        this.iterateRows((rowIndex) => {
            const background = this.getSpriteFromPool('reelsBg');
            const symbol = this.createReelSymbol();

            symbol.init(this.getSpriteFromPool('symbols'));
            symbol.setSymbol(this._strip[this.makeNormalizedPosition(rowIndex + this._currentPosition)]);
            symbol.index = rowIndex;
            symbol.rubySymbol = this.rubySymbol;
            symbol.x = this._offsets.x;
            symbol.y = this._offsets.y + this.getYPositionForRow(rowIndex);

            this._symbols[this.rowIndexToSymbolIndex(rowIndex)] = symbol;
            this._container.addChild(symbol);
            symbol.reelSetIndex = this._container.getChildIndex(symbol);

            if (background) {
                background.x = this._offsets.x;
                background.y = this._offsets.y + this.getYPositionForRow(rowIndex);
                this._backgroundContainer!.addChild(background);
            }
        });
    }

    protected createReelSymbol(): ReelSymbol {
        return new ReelSymbol();
    }

    protected makeNormalizedPosition(position: number): number {
        if (position < 0) {
            return (position % this._strip.length) + this._strip.length;
        }

        return position % this._strip.length;
    }

    protected rowIndexToSymbolIndex(rowIndex: number): number {
        return rowIndex + Math.abs(this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET);
    }

    public iterateRows(callBack: RowsIteratorCallback, visibleRows = false): void {
        const symbolsOffSet = visibleRows ? 0 : this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET;
        const rowAmount = visibleRows ? this._reelConstants.ROW_AMOUNT - 1 : this._reelConstants.ROW_AMOUNT;

        if (this._reelConstants.SYMBOLS_ORDERING === SymbolsOrdering.TOP_TO_BOTTOM) {
            for (let rowIndex = symbolsOffSet; rowIndex <= rowAmount; rowIndex++) {
                callBack(rowIndex);
            }
        } else if (this._reelConstants.SYMBOLS_ORDERING === SymbolsOrdering.BOTTOM_TO_TOP) {
            for (let rowIndex = rowAmount; rowIndex >= symbolsOffSet; rowIndex--) {
                callBack(rowIndex);
            }
        }
    }

    protected updateSymbolsOnSpinning(): void {
        this.iterateRows((rowIndex) => {
            this._isSpinning && this.setSymbol(this.makeNormalizedPosition(this._currentPosition + rowIndex), rowIndex);
        });
    }

    protected updateStoppingSymbols(): void {
        this.iterateRows((rowIndex) => {
            let stripPos = this.makeNormalizedPosition(this._currentPosition + rowIndex);

            if (this.isAtPositionForFinalSymbol(rowIndex)) {
                // here we're getting right symbol from stop positions (for final reels positions)
                stripPos = this.makeNormalizedPositionForFinalSymbol(rowIndex);
                this._updatingResultSymbols = true;
            }

            this.setSymbol(stripPos, rowIndex);
        });

        this.reOrderOverlays();
    }

    protected reOrderOverlays(): void {
        this._reelsConfig.defaultSymbolsOrder.forEach((orderSymbolType) => {
            this.iterateRows((rowIndex) => {
                const symbol: ReelSymbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];

                if (this._reelsConfig.oversizeSymbols.indexOf(symbol.symbolType) !== -1 &&
                    this.isSymbolsEqual(orderSymbolType, symbol.symbolType)) {
                    this.setSymbolOnTop(symbol);
                }
            });
        });
    }

    protected isSymbolsEqual(orderSymbol: number | number[], rowSymbol: number): boolean {
        if (orderSymbol instanceof Array) {
            return orderSymbol.indexOf(rowSymbol) !== -1;
        }

        return orderSymbol === rowSymbol;
    }

    protected addTrailAnimation(rowIndex: number): void {
        const animation = this._spineUtils.createSpineAnimation('ruby_coin', 'coin_trail', true);
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];

        symbol.trailAnimation = animation;
        symbol.trailAnimationContainer = new Container();
        symbol.trailAnimationContainer.addChild(animation.container);
        symbol.updateAnimationContainerPosition();
        this._animationToBeMasked.addChild(symbol.trailAnimationContainer);

        animation.container.x = this._reelConstants.SYMBOL.width / 2;
        animation.container.y = this._reelConstants.SYMBOL.height / 2;
        animation.start();
        this.animationUpdater.add(symbol.trailAnimation);
    }

    protected setSymbol(stripPosition: number, rowIndex: number): void {
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];

        symbol.setSymbol(this._strip[stripPosition]);

        if (this.isRubySymbol(symbol.symbolType) && !this.isRubyFrameShouldBeSwitched(rowIndex) &&
            this._isSpinning && this._movedOffset !== 0) {
            this.addTrailAnimation(rowIndex);
        } else {
            symbol.stopTrailAnimation();
        }

        if (this._isBlurSymbols) {
            symbol.addBlur();
        }
    }

    protected isRubyFrameShouldBeSwitched(rowIndex: number): boolean {
        return this._symbolsLeftToStop !== 0 && this._symbolsLeftToStop <= ReelConstants.ROW_AMOUNT
            && this._symbolsLeftToStop + rowIndex >= 0 && this._symbolsLeftToStop + rowIndex < ReelConstants.ROW_AMOUNT;
    }

    protected isAtPositionForFinalSymbol(rowIndex: number): boolean {
        return this.rowIndexToSymbolIndex(rowIndex) <= (this._reelConstants.ROW_AMOUNT + this._additionalBottomResultSymbolsAmount +
            Math.abs(this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET)) - this._symbolsLeftToStop;
    }

    protected makeNormalizedPositionForFinalSymbol(rowIndex: number): number {
        const stopPos: number = this.makeNormalizedPosition(
            this._stopPosition! +
            this._symbolsLeftToStop +
            this.rowIndexToSymbolIndex(rowIndex) +
            this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET);

        return stopPos;
    }

    protected hideSymbolsUnderStoppingAnimations(): void {
        // just update only visible rows symbols
        this.iterateRows((rowIndex) => {
            const symbolId = this._strip[this.makeNormalizedPositionForFinalSymbol(rowIndex)];
            const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];

            if (this.isSymbolHasHitAnimation(symbolId, rowIndex)) {
                // check if we do not have chance to flow freespins
                if (symbolId === this.scatterIndex && !this._scatterOnReelThatTriggerFreeGames![rowIndex]) {
                    return;
                }

                if (this.isRubySymbol(symbolId)) {
                    symbol.setSymbolFrameIndex(RubyFrameIndex.activated, symbolId);
                } else {
                    this.hideSymbolAt(rowIndex);
                }
            }
        }, true);
    }

    protected isSymbolHasHitAnimation(symbol: number, rowIndex: number): boolean {
        let value = false;

        this._symbolsWithHitAnimation.forEach((rows, symbolIndex) => {
            if (symbol === symbolIndex) {
                value = rows.indexOf(rowIndex) >= 0;
            }
        });

        if (!value) {
            value = this._reelsConfig.scatterIndex === symbol;
        }

        return value;
    }

    /**
     * changing order of symbols so that main reel symbols appear on top
     */
    protected updateSymbolsOrder(): void {
        this.iterateRows((rowIndex) => {
            this.setSymbolOnTop(this._symbols[this.rowIndexToSymbolIndex(rowIndex)]);
        });
    }

    protected hideHitSymbols(): void {
        this._symbols.forEach(symbol => symbol.animationContainer && symbol.hideSymbol());
    }

    public updateRubyCoinOnRestore(rowIndex: number): void {
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];

        symbol.setSymbolFrameIndex(RubyFrameIndex.activated, symbol.symbolType);
        symbol.stopTrailAnimation();
    }

    protected isRubySymbol(symbolType: number): boolean {
        return !!this._reelsConfig.rubySymbol && this._reelsConfig.rubySymbol === symbolType;
    }

    public toggleTurboSpin(value: boolean): void {
        this._reelSpeed = value ? this._reelConstants.TURBO_REEL_SPEED_MULTIPLIER : this._reelConstants.REEL_SPEED_MULTIPLIER;
    }

    public get rubySymbol(): number {
        return (this._reelsConfig.rubySymbol ?? null) as number;
    }

    public get reelSymbols(): ReelSymbol[] {
        return this._symbols;
    }

    public set onReelStopping(value: Function) {
        this._onReelStopping = value;
    }

    public set onReelStopped(value: Function) {
        this._onReelStopped = value;
    }

    public set strip(value: number[]) {
        this._strip = value;
    }

    public get strip(): number[] {
        return this._strip;
    }

    public set onReelAnimationFinished(value: Function) {
        this._onReelAnimationFinished = value;
    }

    public set onScatterHitAnimationCallback(value: Function) {
        this._onScatterHitAnimationCallback = value;
    }

    public set scatterOnReelThatTriggerFreeGames(value: number[]) {
        this._scatterOnReelThatTriggerFreeGames = value;
    }

    public set onBounceStoppingStartedCallback(value: Function) {
        this._onBounceStoppingStartedCallback = value;
    }

    public get animationUpdater(): UpdateManager {
        return this._animationUpdater;
    }

    public get isStopped(): boolean {
        return this._isStopped;
    }

    public get isSpinning(): boolean {
        return this._isSpinning;
    }

    public get symbolsWithHitAnimation(): number[][] {
        return this._symbolsWithHitAnimation;
    }

    public set symbolsWithHitAnimation(value: number[][]) {
        this._symbolsWithHitAnimation = value;
    }

    public get oversizeHitSymbols(): number[] {
        return this._oversizeHitSymbols;
    }

    public set oversizeHitSymbols(value: number[]) {
        this._oversizeHitSymbols = value;
    }

    public get isHitAnimationsFinished(): boolean | undefined {
        return this._isHitAnimationsFinished;
    }

    public get stackedSymbols(): StackedSymbol[] {
        return this._stackedSymbols!;
    }

    public get scatterIndex(): number {
        return this._reelsConfig.scatterIndex!;
    }

    public set reelSpeed(value: number) {
        this._reelSpeed = value;
    }
}
